import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Skeleton,
  SvgIcon,
  Stack,
  alpha,
} from '@mui/material';
import {
  Bot,
  Eye,
  MoreVertical,
  Pencil,
  Settings,
  Trash2,
  MessageSquare,
  Layers,
  Calendar,
} from 'lucide-react';
import dayjs from 'dayjs';

const BotCard = ({
  bot,
  loading = false,
  onEdit,
  onDelete,
  onSettings,
  onView,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action) => {
    handleMenuClose();
    action?.(bot);
  };

  if (loading) {
    return (
      <Card
        sx={{
          height: '100%',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
            <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: 2.5 }} />
            <Box sx={{ ml: 2, flex: 1 }}>
              <Skeleton variant="text" width="70%" height={28} />
              <Skeleton variant="rounded" width={70} height={24} sx={{ mt: 0.5, borderRadius: 1.5 }} />
            </Box>
          </Box>
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="85%" />
          <Box sx={{ display: 'flex', gap: 3, mt: 2.5 }}>
            <Skeleton variant="rounded" width={60} height={40} />
            <Skeleton variant="rounded" width={80} height={40} />
            <Skeleton variant="rounded" width={90} height={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const isActive = (() => {
    if (typeof bot?.isActive === 'boolean') return bot.isActive;
    if (bot?.b_active !== undefined) return Number(bot.b_active) === 1;
    if (bot?.b_status !== undefined) return Number(bot.b_status) === 1;
    if (typeof bot?.status === 'number') return bot.status === 1;
    if (typeof bot?.status === 'string') return bot.status.toLowerCase() === 'active';
    return false;
  })();

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        bgcolor: 'background.paper',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'visible',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
          transform: 'translateY(-2px)',
          '& .bot-avatar': {
            transform: 'scale(1.05)',
          },
        },
        '&::before': isActive ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          bgcolor: 'success.main',
          borderRadius: '12px 12px 0 0',
        } : {},
      }}
      onClick={() => onView?.(bot)}
    >
      <CardContent sx={{ flex: 1, p: 2.5, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar
            className="bot-avatar"
            sx={{
              width: 52,
              height: 52,
              borderRadius: 2.5,
              background: isActive
                ? (theme) => `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
                : (theme) => `linear-gradient(135deg, ${theme.palette.grey[300]} 0%, ${theme.palette.grey[400]} 100%)`,
              color: '#fff',
              transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <SvgIcon component={Bot} inheritViewBox sx={{ fontSize: 26 }} />
          </Avatar>
          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '1.1rem',
                lineHeight: 1.3,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {bot?.name || bot?.bot_name}
            </Typography>
            <Chip
              label={isActive ? 'Active' : 'Inactive'}
              size="small"
              sx={{
                mt: 0.75,
                height: 24,
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: 1.5,
                bgcolor: isActive
                  ? (theme) => alpha(theme.palette.success.main, 0.12)
                  : (theme) => alpha(theme.palette.grey[500], 0.12),
                color: isActive ? 'success.dark' : 'text.secondary',
                border: 'none',
                '& .MuiChip-label': {
                  px: 1.25,
                },
              }}
            />
          </Box>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              ml: 0.5,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
              },
            }}
          >
            <MoreVertical size={18} />
          </IconButton>
        </Box>

        <Typography
          variant="body2"
          sx={{
            mb: 2.5,
            color: 'text.secondary',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: 44,
          }}
        >
          {bot?.description || 'No description available'}
        </Typography>

        <Stack
          direction="row"
          spacing={2.5}
          sx={{
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Layers size={14} color="#9e9e9e" />
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                Blocks
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {bot?.blocks_count || bot?.blocks || 0}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <MessageSquare size={14} color="#9e9e9e" />
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                Conversations
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {bot?.conversations || 0}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Calendar size={14} color="#9e9e9e" />
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                Created
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {dayjs(bot?.createdAt || bot?.created_at || bot?.bot_created).format('MMM DD, YYYY')}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </CardContent>

      <CardActions
        sx={{
          px: 2.5,
          pb: 2,
          pt: 0.5,
          justifyContent: 'flex-start',
          gap: 0.5,
        }}
      >
        <Tooltip title="Open Builder" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(bot);
            }}
            sx={{
              color: 'success.main',
              bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.16),
              },
            }}
          >
            <SvgIcon component={Eye} inheritViewBox sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(bot);
            }}
            sx={{
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
              },
            }}
          >
            <SvgIcon component={Pencil} inheritViewBox sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Settings" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onSettings?.(bot);
            }}
            sx={{
              color: 'warning.main',
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.16),
              },
            }}
          >
            <SvgIcon component={Settings} inheritViewBox sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </CardActions>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            minWidth: 180,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1.25,
              borderRadius: 1,
              mx: 1,
              my: 0.25,
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            },
          },
        }}
      >
        <MenuItem onClick={() => handleAction(onView)}>
          <ListItemIcon>
            <SvgIcon component={Eye} inheritViewBox sx={{ fontSize: 18, color: 'success.main' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction(onEdit)}>
          <ListItemIcon>
            <SvgIcon component={Pencil} inheritViewBox sx={{ fontSize: 18, color: 'primary.main' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Edit Bot</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction(onSettings)}>
          <ListItemIcon>
            <SvgIcon component={Settings} inheritViewBox sx={{ fontSize: 18, color: 'warning.main' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Settings</ListItemText>
        </MenuItem>
        <Box sx={{ my: 1, mx: 2, borderTop: '1px solid', borderColor: 'divider' }} />
        <MenuItem onClick={() => handleAction(onDelete)} sx={{ '&:hover': { bgcolor: (theme) => `${alpha(theme.palette.error.main, 0.08)} !important` } }}>
          <ListItemIcon>
            <SvgIcon component={Trash2} inheritViewBox sx={{ fontSize: 18, color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default BotCard;
